import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Users, Loader2, Download, Share2, Image, FileText, MessageCircle, Wifi } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import TournamentBracket from "../components/TournamentBracket";
import wsService from "../lib/websocket";

export default function TournamentDetail() {
  const { tournamentId } = useParams();
  const { wsConnected } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [matchDialog, setMatchDialog] = useState(null);
  const [matchResult, setMatchResult] = useState({ score1: "", score2: "" });
  const [submitting, setSubmitting] = useState(false);
  const [liveUpdate, setLiveUpdate] = useState(null);
  const bracketRef = useRef(null);

  const fetchTournament = async () => {
    try {
      const response = await api.getTournament(tournamentId);
      setTournament(response.data);
    } catch (error) {
      console.error("Error fetching tournament:", error);
      toast.error("Turnuva yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTournament();
    
    // Subscribe to tournament updates via WebSocket
    wsService.subscribeToTournament(tournamentId);
    
    const unsubscribe = wsService.on('tournament_update', (data) => {
      if (data.tournament_id === tournamentId) {
        // Show live update notification
        setLiveUpdate(data);
        
        if (data.data?.tournament_completed) {
          toast.success(`🏆 Turnuva tamamlandı! Şampiyon: ${data.data.winner_name}`);
        } else {
          toast.success(`🔴 Canlı: ${data.data?.winner_name} maçı kazandı! (${data.data?.score})`);
        }
        
        // Refresh data
        fetchTournament();
        
        // Clear notification after 5 seconds
        setTimeout(() => setLiveUpdate(null), 5000);
      }
    });
    
    return () => {
      unsubscribe();
      wsService.unsubscribe('tournament', tournamentId);
    };
  }, [tournamentId]);

  const handleMatchClick = (match) => {
    if (match.status === "completed" || match.status === "waiting") return;
    if (!match.player1 || !match.player2) return;
    setMatchDialog(match);
    setMatchResult({ score1: "", score2: "" });
  };

  const submitMatchResult = async () => {
    if (!matchDialog) return;
    
    const score1 = parseInt(matchResult.score1) || 0;
    const score2 = parseInt(matchResult.score2) || 0;
    
    if (score1 === score2) {
      toast.error("Berabere olamaz, bir kazanan olmalı");
      return;
    }

    setSubmitting(true);
    try {
      const winnerId = score1 > score2 ? matchDialog.player1.user_id : matchDialog.player2.user_id;
      
      await api.updateMatchResult(tournamentId, matchDialog.match_id, {
        winner_id: winnerId,
        score_player1: score1,
        score_player2: score2
      });
      
      toast.success("Maç sonucu kaydedildi");
      setMatchDialog(null);
      fetchTournament();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sonuç kaydedilemedi");
    } finally {
      setSubmitting(false);
    }
  };

  const exportAsImage = async () => {
    if (!bracketRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(bracketRef.current, {
        backgroundColor: "#050E2E",
        scale: 2
      });
      
      const link = document.createElement("a");
      link.download = `${tournament.name.replace(/\s+/g, "_")}_bracket.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      
      toast.success("Görsel indirildi");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Görsel oluşturulamadı");
    } finally {
      setExporting(false);
    }
  };

  const exportAsPDF = async () => {
    if (!bracketRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(bracketRef.current, {
        backgroundColor: "#050E2E",
        scale: 2
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "landscape" : "portrait",
        unit: "px",
        format: [canvas.width, canvas.height]
      });
      
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`${tournament.name.replace(/\s+/g, "_")}_bracket.pdf`);
      
      toast.success("PDF indirildi");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("PDF oluşturulamadı");
    } finally {
      setExporting(false);
    }
  };

  const shareToWhatsApp = async () => {
    // This is a mock - will show toast about mock status
    toast.info("WhatsApp paylaşımı şu an mock modda. Twilio API anahtarları eklendiğinde aktif olacak.", {
      duration: 5000
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <p className="text-[#B1C1C1]">Turnuva bulunamadı</p>
      </div>
    );
  }

  const winner = tournament.winner_id 
    ? tournament.players?.find(p => p.user_id === tournament.winner_id)
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tournament-detail-page">
      {/* Live Update Banner */}
      {liveUpdate && (
        <div className="mb-4 p-3 rounded-lg bg-green-500/20 border border-green-500/50 animate-pulse">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping" />
            <span className="text-green-400 text-sm font-medium">
              Canlı: {liveUpdate.data?.winner_name} maçı kazandı! ({liveUpdate.data?.score})
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              {tournament.name}
            </h1>
            {wsConnected && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20" title="Canlı bağlantı aktif">
                <Wifi className="w-3 h-3 text-green-400" />
                <span className="text-xs text-green-400">CANLI</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-[#C6A87C]">{tournament.game_name}</span>
            <span className="text-[#64748B]">•</span>
            <span className="text-[#B1C1C1]">
              {tournament.tournament_type === "single_elimination" ? "Tek Eleme" : "Çift Eleme"}
            </span>
            <span className="text-[#64748B]">•</span>
            <span className="text-[#B1C1C1]">{tournament.players?.length || 0} Oyuncu</span>
          </div>
        </div>
        
        {/* Export Buttons */}
        <div className="flex items-center gap-2">
          <Button
            onClick={exportAsImage}
            disabled={exporting}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="export-image-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
            PNG
          </Button>
          <Button
            onClick={exportAsPDF}
            disabled={exporting}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="export-pdf-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            PDF
          </Button>
          <Button
            onClick={shareToWhatsApp}
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            data-testid="share-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
        </div>
      </div>

      {/* Winner Banner */}
      {winner && (
        <div className="glass-card p-6 mb-6 bg-gradient-to-r from-[#C6A87C]/20 to-transparent border-[#C6A87C]/30">
          <div className="flex items-center justify-center gap-4">
            <Trophy className="w-10 h-10 text-[#C6A87C]" />
            <div className="text-center">
              <p className="text-[#C6A87C] text-sm uppercase tracking-wider">Şampiyon</p>
              <p className="text-2xl font-bold text-white">{winner.name}</p>
            </div>
            <Trophy className="w-10 h-10 text-[#C6A87C]" />
          </div>
        </div>
      )}

      {/* Bracket */}
      <div className="glass-card overflow-hidden">
        <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Turnuva Şeması
            </h2>
          </div>
        </div>
        
        <div className="p-6 overflow-x-auto" ref={bracketRef}>
          <TournamentBracket 
            matches={tournament.matches || []} 
            tournamentType={tournament.tournament_type}
            onMatchClick={handleMatchClick}
          />
        </div>
      </div>

      {/* Players List */}
      <div className="glass-card mt-6 overflow-hidden">
        <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#4DFFFF]" />
            <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Katılımcılar
            </h2>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex flex-wrap gap-3">
            {tournament.players?.map((player) => (
              <Link
                key={player.user_id}
                to={`/profile/${player.username}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  player.user_id === tournament.winner_id
                    ? "bg-[#C6A87C]/20 border border-[#C6A87C]/30"
                    : "bg-white/5 hover:bg-white/10"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  player.user_id === tournament.winner_id
                    ? "bg-[#C6A87C]/30"
                    : "bg-[#4DFFFF]/20"
                }`}>
                  <span className={`text-sm font-bold ${
                    player.user_id === tournament.winner_id
                      ? "text-[#C6A87C]"
                      : "text-[#4DFFFF]"
                  }`}>
                    {player.name?.charAt(0)}
                  </span>
                </div>
                <span className="text-white">{player.name}</span>
                {player.user_id === tournament.winner_id && (
                  <Trophy className="w-4 h-4 text-[#C6A87C]" />
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Match Result Dialog */}
      <Dialog open={!!matchDialog} onOpenChange={() => setMatchDialog(null)}>
        <DialogContent className="bg-[#0A1635] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-['Barlow_Condensed'] uppercase tracking-wider">
              Maç Sonucu Gir
            </DialogTitle>
          </DialogHeader>
          
          {matchDialog && (
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 text-center">
                  <p className="text-white font-bold">{matchDialog.player1?.name}</p>
                  <Input
                    type="number"
                    min="0"
                    value={matchResult.score1}
                    onChange={(e) => setMatchResult({ ...matchResult, score1: e.target.value })}
                    className="mt-2 bg-[#050E2E]/50 border-white/10 text-white text-center text-2xl h-14"
                    placeholder="0"
                  />
                </div>
                <span className="text-2xl text-[#64748B]">vs</span>
                <div className="flex-1 text-center">
                  <p className="text-white font-bold">{matchDialog.player2?.name}</p>
                  <Input
                    type="number"
                    min="0"
                    value={matchResult.score2}
                    onChange={(e) => setMatchResult({ ...matchResult, score2: e.target.value })}
                    className="mt-2 bg-[#050E2E]/50 border-white/10 text-white text-center text-2xl h-14"
                    placeholder="0"
                  />
                </div>
              </div>
              
              <Button
                onClick={submitMatchResult}
                disabled={submitting}
                className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Sonucu Kaydet"
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
